import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { BadgeLink, type BadgeProps } from '@gitbook/spine-react';

import {
    type FeatureUpgradeAction,
    isAIUnavailableAction,
    isSiteUpgradeAction,
    useSiteContext,
    useTrialingSiteFeaturePlan,
} from '../../state';
import { getTrialingBadgeProps, TrialingBadge } from '../OrganizationBilling';

/**
 * A small badge indicating that a site feature requires a paid plan.
 *
 * During an active trial, it displays "Trialing" with the required plan in its tooltip.
 * Otherwise, it displays the required organization or site upgrade.
 *
 * Can, optionally, render as an inactive paid feature indicator. Used to warn the
 * user that the feature is enabled and can be disabled, but an upgrade will be
 * required to enable it again.
 */
export function PaidFeatureIndicator({
    upgrade,
    featureKey,
    isFeatureActive,
    iconOnly,
    size,
    thin,
}: {
    upgrade: FeatureUpgradeAction | undefined;
    featureKey: api.SiteFeatureKey;
    /**
     * `danger` colored and Icon.Warning, instead of `upgrade` colored and Icon.Upgrade
     * the tooltip explains that the feature, if disabled, will require an upgrade to
     * be re-enabled.
     */
    isFeatureActive?: boolean;
} & Pick<BadgeProps, 'iconOnly' | 'thin' | 'size'>) {
    const { site } = useSiteContext();
    const trialingPlan = useTrialingSiteFeaturePlan(site, featureKey);

    if (trialingPlan && !upgrade) {
        return (
            <TrialingBadge
                plan={trialingPlan}
                {...getPaidIndicatorBadgeStyleProps({ iconOnly, size, thin })}
            />
        );
    }

    if (!upgrade) {
        return null;
    }

    // Plan-based copy only fits site upgrades; other actions carry their own tooltip.
    const tooltip = isSiteUpgradeAction(upgrade)
        ? isFeatureActive
            ? `This feature is part of the ${upgrade.plan} plan. You need to upgrade to re-enable it.`
            : `Available on the ${upgrade.plan} plan. Click to upgrade.`
        : typeof upgrade.tooltip === 'string'
          ? upgrade.tooltip
          : (upgrade.tooltip?.label ?? undefined);

    return (
        <BadgeLink
            {...getPaidIndicatorBadgeProps({ upgrade, iconOnly, size, thin, isFeatureActive })}
            tooltip={tooltip}
            href={upgrade.href}
            target={upgrade.target}
            onPress={upgrade.onPress}
        />
    );
}

/**
 * Returns badge props for paid feature indicators.
 * Some components, like Picker or Palette button, may use this function for their `badge` prop.
 *
 */
export function getPaidIndicatorBadgeProps({
    upgrade,
    isFeatureActive,
    trialingPlan,
    ...badgeProps
}: BadgeProps & {
    upgrade: FeatureUpgradeAction | undefined;
    trialingPlan?: string | null;
    // `danger` colored and Icon.Warning, instead of `upgrade` colored and Icon.Upgrade
    isFeatureActive?: boolean;
}): BadgeProps | undefined {
    const commonProps = getPaidIndicatorBadgeStyleProps(badgeProps);

    if (trialingPlan && !upgrade) {
        return getTrialingBadgeProps({
            ...badgeProps,
            ...commonProps,
            plan: trialingPlan,
        });
    }

    if (!upgrade) {
        return undefined;
    }

    const label = isSiteUpgradeAction(upgrade)
        ? upgrade.plan
        : (upgrade.badgeLabel ?? upgrade.label);

    if (isAIUnavailableAction(upgrade)) {
        return {
            kind: 'secondary',
            icon: Icon.AI,
            label: !badgeProps.iconOnly ? label : undefined,
            ...badgeProps,
            ...commonProps,
        };
    }

    if (isFeatureActive) {
        return {
            kind: 'danger',
            icon: Icon.Warning,
            label: !badgeProps.iconOnly ? label : undefined,
            ...badgeProps,
            ...commonProps,
        };
    }

    return {
        kind: 'upgrade',
        icon: Icon.Upgrade,
        label: !badgeProps.iconOnly ? label : undefined,
        ...badgeProps,
        ...commonProps,
    };
}

function getPaidIndicatorBadgeStyleProps(
    props: Pick<BadgeProps, 'iconOnly' | 'size' | 'thin'>
): Pick<BadgeProps, 'iconOnly' | 'size' | 'thin'> {
    const { iconOnly, size, thin } = props;

    return {
        iconOnly,
        size: size ?? (iconOnly ? 'medium' : 'small'),
        thin: thin ?? !iconOnly,
    };
}
